/**
 * Seedable pseudo-random number generator (mulberry32).
 * Deterministic given a seed — essential for reproducible games and tests.
 */
export interface Rng {
  /** Float in [0, 1). */
  next(): number;
  /** Integer in [0, n). */
  int(n: number): number;
  /** In-place Fisher–Yates shuffle. Returns the same array. */
  shuffle<T>(arr: T[]): T[];
  /** Pick a random element. */
  pick<T>(arr: readonly T[]): T;
}

export function makeRng(seed = Date.now() >>> 0): Rng {
  return rngFromState({ a: seed >>> 0 });
}

/**
 * The same generator over a state you hold: `state.a` moves on with every draw, so saving it and
 * starting again from the saved value continues the exact same sequence (a saved game re-deals
 * the very hand it was saved in).
 */
export function rngFromState(state: { a: number }): Rng {
  const next = (): number => {
    let a = state.a | 0;
    a = (a + 0x6d2b79f5) | 0;
    state.a = a;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const int = (n: number): number => Math.floor(next() * n);
  return {
    next,
    int,
    shuffle<T>(arr: T[]): T[] {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = int(i + 1);
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    },
    pick<T>(arr: readonly T[]): T {
      return arr[int(arr.length)];
    },
  };
}
